import MobileTooltip from "@/components/common/MobileTooltip";
import { useBetaStatus } from "@/hooks/useBetaStatus";
import type { BrandRatesItem } from "@/services/api/BrandRates";
import { formatPercent } from "@/utils/format";
import { Grid } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
// import { brandComparisonApi } from '@/services/api/brandComparison'
// import type { BrandComparisonData } from '@/services/mock'

interface Row {
  brand: string;
  mentionRate: number;
  firstMentionRate: number;
  firstRecommendRate: number;
  positiveSentiment: number;
  negativeMentionRate: number;
  isCurrent?: boolean;
}

interface CompetitorOverviewSectionProps {
  rows?: Row[];
  brandRatesList?: BrandRatesItem[];
  title?: string;
  onDownload?: () => void;
}

const CompetitorOverviewSection: React.FC<CompetitorOverviewSectionProps> = ({
  rows: propRows,
  brandRatesList,
  title,
  onDownload,
}) => {
  const { t } = useTranslation();
  const effectiveTitle = title || t("search_detail.competitor_title");
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const [rows, setRows] = useState<Row[]>(propRows || []);
  const [loading] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);

  // 使用自定义Hook管理beta状态
  const { isBeta } = useBetaStatus();
  const [tipMaxWidth, setTipMaxWidth] = useState(300);
  const rootRef = useRef<HTMLDivElement>(null);
  const tipAnchorRef = useRef<HTMLDivElement>(null);

  // 添加 CSS 动画样式
  React.useEffect(() => {
    const styleId = "competitor-overview-styles";
    if (document.getElementById(styleId)) return;

    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);

    return () => {
      const styleElement = document.getElementById(styleId);
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);

  // 优先使用新接口的 brandRatesList 聚合结果映射到表格行
  useEffect(() => {
    if (Array.isArray(brandRatesList) && brandRatesList.length > 0) {
      const mapped: Row[] = brandRatesList.map((item) => ({
        brand: item.brandName,
        mentionRate: item.mentionRate,
        firstMentionRate: item.firstMentionRate,
        firstRecommendRate: item.firstRecommendationRate,
        positiveSentiment: item.positiveMentionRate,
        negativeMentionRate: item.negativeMentionRate,
        isCurrent: !!item.isCurBrand,
      }));
      setRows(mapped);
    } else if (propRows) {
      setRows(propRows);
    }
  }, [brandRatesList, propRows]);

  // 计算Tooltip最大宽度
  React.useEffect(() => {
    const calc = () => {
      if (!rootRef.current) return;
      const rect = rootRef.current.getBoundingClientRect();
      const available = Math.min(300, window.innerWidth - rect.left - 20);
      setTipMaxWidth(available);
    };
    const ro = new ResizeObserver(calc);
    if (rootRef.current) ro.observe(rootRef.current);
    calc();
    window.addEventListener("scroll", calc, { passive: true });
    window.addEventListener("resize", calc);

    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", calc as any);
      window.removeEventListener("resize", calc as any);
    };
  }, []);
  React.useEffect(() => {
    const close = () => setTipOpen(false);
    const container = rootRef.current;
    window.addEventListener("scroll", close, { passive: true });
    container?.addEventListener("scroll", close, { passive: true } as any);
    return () => {
      window.removeEventListener("scroll", close as any);
      container?.removeEventListener("scroll", close as any);
    };
  }, []);

  // 优先使用从 brandRatesList 映射到的本地 rows；没有再回退到 propRows
  const displayRows = rows && rows.length > 0 ? rows : propRows || [];
  return (
    <div
      ref={rootRef}
      style={{
        marginTop: 16,
        background: "#ffffff",
        borderRadius: 6,
        padding: isMobile ? 10 : 20,
        boxShadow: "0 2px 8px -2px rgba(0,0,0,.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 12,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{ fontSize: 18, fontWeight: "normal", color: "#000000" }}
          >
            {effectiveTitle}
          </span>
          <MobileTooltip
            content={t("api.industry_top10_tooltip")}
            title={
              effectiveTitle + " - " + t("search_detail.insight_metric_desc")
            }
          />
        </div>
        {/* 暂时隐藏查看更多按钮 */}
        {/* <button
          type="button"
          style={{
            padding: '8px 14px',
            background: 'linear-gradient(135deg, #21C4B8 0%, #4BA67A 100%)',
            color: '#ffffff',
            border: '1px solid rgba(255,255,255,0.6)',
            borderRadius: 6,
            fontWeight: 700,
            cursor: 'pointer',
            boxShadow: '0 6px 12px rgba(15,108,117,0.20)'
          }}
          onClick={onDownload}
        >{t('common.charts.intent_btn_more')}</button> */}
      </div>
      <div
        style={{
          border: "1px solid #e5e7eb",
          borderRadius: 6,
          overflow: "hidden",
          background: "#ffffff",
          position: "relative",
        }}
      >
        <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              minWidth: "600px",
            }}
          >
            <thead>
              <tr
                style={{
                  background: "#f9fafb",
                  textAlign: "center",
                  fontWeight: 600,
                }}
              >
                <th
                  style={{
                    padding: "12px 0px 12px 16px",
                    color: "#374151",
                    whiteSpace: "nowrap",
                    textAlign: "left",
                  }}
                >
                  {t("home_components.chart_brand")}
                </th>
                <th
                  style={{
                    padding: "12px 0px",
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("api.metric_mention_rate")}
                </th>
                <th
                  style={{
                    padding: "12px 0px",
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("search_detail.competitor_first_mention")}
                </th>
                <th
                  style={{
                    padding: "12px 0px",
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("search_detail.competitor_first_recommend")}
                </th>
                <th
                  style={{
                    padding: "12px 0px",
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("search_detail.competitor_positive_rate")}
                </th>
                <th
                  style={{
                    padding: "12px 0px",
                    color: "#374151",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("search_detail.competitor_negative_rate")}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: "40px 16px",
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          width: 16,
                          height: 16,
                          border: "2px solid #21C4B8",
                          borderTop: "2px solid transparent",
                          borderRadius: "50%",
                          animation: "spin 1s linear infinite",
                        }}
                      />
                      {t("search_detail.competitor_loading")}
                    </div>
                  </td>
                </tr>
              ) : displayRows.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    style={{
                      padding: "40px 16px",
                      textAlign: "center",
                      color: "#64748b",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 8,
                      }}
                    >
                      <div style={{ fontSize: 48, opacity: 0.3 }}>📊</div>
                      <div>{t("search_detail.competitor_no_data")}</div>
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        {t("search_detail.competitor_check_select")}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                displayRows.map((r, idx) => (
                  <tr
                    key={idx}
                    style={{
                      background: r.isCurrent
                        ? "#f0f9ff"
                        : idx % 2 === 0
                          ? "#ffffff"
                          : "#f9fafb",
                      textAlign: "center",
                      borderBottom:
                        idx < displayRows.length - 1
                          ? "1px solid #e5e7eb"
                          : "none",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px 0px 12px 16px",
                        borderBottom: "1px solid #f3f4f6",
                        color: "#111827",
                        fontWeight: r.isCurrent ? 600 : 400,
                        minWidth: "120px",
                        textAlign: "left",
                      }}
                    >
                      {r.brand}
                      {r.isCurrent && (
                        <span
                          style={{
                            color: "#21C4B8",
                            marginLeft: "4px",
                            fontSize: 12,
                          }}
                        >
                          {t("search_detail.competitor_mark_current")}
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        padding: "12px 0px",
                        borderBottom: "1px solid #f3f4f6",
                        color: "#111827",
                        minWidth: "100px",
                      }}
                    >
                      {formatPercent(r.mentionRate, 1)}
                    </td>
                    <td
                      style={{
                        padding: "12px 0px",
                        borderBottom: "1px solid #f3f4f6",
                        color: "#111827",
                        minWidth: "100px",
                      }}
                    >
                      {formatPercent(r.firstMentionRate, 1)}
                    </td>
                    <td
                      style={{
                        padding: "12px 0px",
                        borderBottom: "1px solid #f3f4f6",
                        color: "#111827",
                        minWidth: "100px",
                      }}
                    >
                      {formatPercent(r.firstRecommendRate, 1)}
                    </td>
                    <td
                      style={{
                        padding: "12px 0px",
                        borderBottom: "1px solid #f3f4f6",
                        color: "#111827",
                        minWidth: "100px",
                      }}
                    >
                      {formatPercent(r.positiveSentiment, 1)}
                    </td>
                    <td
                      style={{
                        padding: "12px 0px",
                        borderBottom: "1px solid #f3f4f6",
                        color: "#111827",
                        minWidth: "100px",
                      }}
                    >
                      {formatPercent(r.negativeMentionRate, 1)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 解锁覆盖层 - 只在beta版本且数据超过5条时显示，只遮盖下面的5行数据 */}
        {isBeta && rows.length > 5 && (
          <div
            style={{
              position: "absolute",
              top: "281px", // 从第6行开始
              left: 0, // 从表格左边缘开始
              right: 0, // 到表格右边缘
              bottom: 0, // 到表格底部
              background: "rgba(59, 130, 246, 0.1)",
              backdropFilter: "blur(4px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 8,
              cursor: "pointer",
              zIndex: 10,
              transition: "all 0.2s ease",
            }}
            onClick={() => {
              // 执行原有的下载功能
              onDownload && onDownload();

              // 平滑滚动到 NewContactUs 组件中的表单
              setTimeout(() => {
                const el = document.getElementById("new-contact-form");
                if (el) {
                  el.scrollIntoView({ behavior: "smooth", block: "start" });
                }
              }, 100);
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                background: "transparent",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg width="32" height="32" viewBox="0 0 1024 1024" fill="white">
                <path d="M771.09045312 471.64751562L252.89970312 471.64751562c-44.7575625 0-81.03670312 36.20432813-81.03670312 80.87625001l0 343.07339062c0 44.67290625 36.27914062 80.87625 81.03670312 80.87625l518.19173438 0c44.75854688 0 81.04753125-36.20334375 81.04753125-80.87625L852.13896875 552.52376563C852.13798437 507.85184375 815.849 471.64751562 771.09045312 471.64751562zM546.12434375 721.73979687l0 90.04964063c0 18.8409375-15.26864062 34.13123438-34.12926563 34.13123438-18.84979688 0-34.12926563-15.29029687-34.12926562-34.13123438l0-90.04964062c-20.37951562-11.804625-34.13025-33.80835938-34.13025-59.06348438 0-37.7015625 30.55795313-68.2605 68.25951562-68.2605s68.27034375 30.5589375 68.27034375 68.2605C580.26542188 687.9314375 566.52551562 709.93517188 546.12434375 721.73979687zM511.99507812 147.37175c-131.95251562 0-238.92946875 105.46889063-238.92946875 244.04034375l1e-8 105.98273437 477.87075 22.75284375L750.93635938 391.41209375C750.93635938 252.84064062 643.95940625 147.37175 511.99507812 147.37175zM341.36745312 491.406875L341.36745312 389.03876562c0-94.2204375 76.395375-170.627625 170.62664063-170.627625 94.24209375 0 170.627625 76.4071875 170.627625 170.627625l0 104.76309375L341.36745312 491.406875z" />
              </svg>
            </div>
            <span style={{ color: "#374151", fontSize: 14, fontWeight: 500 }}>
              {t("search_detail.competitor_join_beta")}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitorOverviewSection;
